import type { Matrix } from '../geom/Matrix';
import type { BitmapData } from './BitmapData';
import { TRect, TWIPS, invalidRect, rectUnion, rectValid } from '../utils/numeric';

/** Recorded vector command (pixels, local space). drawRect/drawCircle/... are expanded to paths like Flash does. */
export type GraphicsCommand =
  | { op: 'beginFill'; color: number; alpha: number }
  | {
      op: 'beginGradientFill';
      type: string;
      colors: number[];
      alphas: number[];
      ratios: number[];
      matrix: Matrix | null;
      spreadMethod: string;
      interpolationMethod: string;
      focalPointRatio: number;
    }
  | { op: 'beginBitmapFill'; bitmap: BitmapData; matrix: Matrix | null; repeat: boolean; smooth: boolean }
  | { op: 'endFill' }
  | {
      op: 'lineStyle';
      thickness: number;
      color: number;
      alpha: number;
      pixelHinting: boolean;
      scaleMode: string;
      caps: string | null;
      joints: string | null;
      miterLimit: number;
    }
  | { op: 'moveTo'; x: number; y: number }
  | { op: 'lineTo'; x: number; y: number }
  | { op: 'curveTo'; cx: number; cy: number; x: number; y: number };

interface FillPath {
  /** Flattened polygons (closed implicitly). */
  polys: number[][];
}

/**
 * flash.display.Graphics: records drawing commands for the renderer and computes bounds
 * (in twips, strokes included) and fill hit tests (even-odd on flattened paths).
 */
export class Graphics {
  /** @internal */ $commands: GraphicsCommand[];
  /** @internal */ $owner: any;
  /** @internal */ $bounds: TRect;
  /** @internal */ $fills: FillPath[];
  /** @internal */ $curFill: FillPath | null;
  /** @internal */ $penX: number;
  /** @internal */ $penY: number;
  /** @internal */ $lineWidth: number;
  /** @internal */ $version: number;

  constructor(owner?: any) {
    this.$commands = [];
    this.$owner = owner ?? null;
    this.$bounds = invalidRect();
    this.$fills = [];
    this.$curFill = null;
    this.$penX = 0;
    this.$penY = 0;
    this.$lineWidth = NaN;
    this.$version = 0;
  }

  /** Commands for the renderer (read-only view). */
  get commands(): readonly GraphicsCommand[] {
    return this.$commands;
  }

  private push(cmd: GraphicsCommand): void {
    this.$commands.push(cmd);
    this.$version++;
    if (this.$owner) this.$owner.$invalidate();
  }

  private addPoint(x: number, y: number, pad: number): void {
    const r: TRect = {
      xMin: Math.round((x - pad) * TWIPS),
      yMin: Math.round((y - pad) * TWIPS),
      xMax: Math.round((x + pad) * TWIPS),
      yMax: Math.round((y + pad) * TWIPS),
    };
    this.$bounds = rectUnion(this.$bounds, r);
  }

  private strokePad(): number {
    const w = this.$lineWidth;
    if (!(w === w)) return 0; // no line style
    return Math.max(w, 1) / 2;
  }

  private fillPoint(x: number, y: number, newSub: boolean): void {
    const f = this.$curFill;
    if (!f) return;
    if (newSub || f.polys.length === 0) f.polys.push([x, y]);
    else f.polys[f.polys.length - 1].push(x, y);
  }

  clear(): void {
    this.$commands = [];
    this.$bounds = invalidRect();
    this.$fills = [];
    this.$curFill = null;
    this.$penX = 0;
    this.$penY = 0;
    this.$lineWidth = NaN;
    this.$version++;
    if (this.$owner) this.$owner.$invalidate();
  }

  private startFill(): void {
    this.$curFill = { polys: [[this.$penX, this.$penY]] };
    this.$fills.push(this.$curFill);
  }

  beginFill(color: number, alpha: number = 1): void {
    this.push({ op: 'beginFill', color: color >>> 0 & 0xffffff, alpha: +alpha });
    this.startFill();
  }

  beginGradientFill(
    type: string,
    colors: number[],
    alphas: number[],
    ratios: number[],
    matrix?: Matrix,
    spreadMethod: string = 'pad',
    interpolationMethod: string = 'rgb',
    focalPointRatio: number = 0,
  ): void {
    this.push({
      op: 'beginGradientFill',
      type,
      colors: colors.slice(),
      alphas: alphas.slice(),
      ratios: ratios.slice(),
      matrix: matrix ? matrix.clone() : null,
      spreadMethod,
      interpolationMethod,
      focalPointRatio,
    });
    this.startFill();
  }

  beginBitmapFill(bitmap: BitmapData, matrix?: Matrix, repeat: boolean = true, smooth: boolean = false): void {
    this.push({ op: 'beginBitmapFill', bitmap, matrix: matrix ? matrix.clone() : null, repeat, smooth });
    this.startFill();
  }

  endFill(): void {
    this.push({ op: 'endFill' });
    this.$curFill = null;
  }

  lineStyle(
    thickness: number = NaN,
    color: number = 0,
    alpha: number = 1,
    pixelHinting: boolean = false,
    scaleMode: string = 'normal',
    caps: string | null = null,
    joints: string | null = null,
    miterLimit: number = 3,
  ): void {
    this.$lineWidth = thickness === undefined || thickness === null ? NaN : +thickness;
    this.push({ op: 'lineStyle', thickness: this.$lineWidth, color: color >>> 0 & 0xffffff, alpha: +alpha, pixelHinting, scaleMode, caps, joints, miterLimit });
  }

  moveTo(x: number, y: number): void {
    x = +x;
    y = +y;
    this.push({ op: 'moveTo', x, y });
    this.$penX = x;
    this.$penY = y;
    this.fillPoint(x, y, true);
  }

  lineTo(x: number, y: number): void {
    x = +x;
    y = +y;
    this.push({ op: 'lineTo', x, y });
    const pad = this.strokePad();
    this.addPoint(this.$penX, this.$penY, pad);
    this.addPoint(x, y, pad);
    this.$penX = x;
    this.$penY = y;
    this.fillPoint(x, y, false);
  }

  curveTo(cx: number, cy: number, ax: number, ay: number): void {
    cx = +cx;
    cy = +cy;
    ax = +ax;
    ay = +ay;
    this.push({ op: 'curveTo', cx, cy, x: ax, y: ay });
    const pad = this.strokePad();
    const x0 = this.$penX;
    const y0 = this.$penY;
    this.addPoint(x0, y0, pad);
    this.addPoint(ax, ay, pad);
    // Exact extrema of the quadratic Bezier.
    for (const [p0, p1, p2, axis] of [
      [x0, cx, ax, 0],
      [y0, cy, ay, 1],
    ] as const) {
      const den = p0 - 2 * p1 + p2;
      if (den !== 0) {
        const t = (p0 - p1) / den;
        if (t > 0 && t < 1) {
          const bx = (1 - t) * (1 - t) * x0 + 2 * (1 - t) * t * cx + t * t * ax;
          const by = (1 - t) * (1 - t) * y0 + 2 * (1 - t) * t * cy + t * t * ay;
          void axis;
          this.addPoint(bx, by, pad);
        }
      }
    }
    if (this.$curFill) {
      for (let i = 1; i <= 8; i++) {
        const t = i / 8;
        this.fillPoint((1 - t) * (1 - t) * x0 + 2 * (1 - t) * t * cx + t * t * ax, (1 - t) * (1 - t) * y0 + 2 * (1 - t) * t * cy + t * t * ay, false);
      }
    }
    this.$penX = ax;
    this.$penY = ay;
  }

  drawRect(x: number, y: number, w: number, h: number): void {
    this.moveTo(x, y);
    this.lineTo(x + w, y);
    this.lineTo(x + w, y + h);
    this.lineTo(x, y + h);
    this.lineTo(x, y);
  }

  drawRoundRect(x: number, y: number, w: number, h: number, ew: number, eh: number = NaN): void {
    if (!(eh === eh)) eh = ew;
    const rx = Math.min(Math.abs(ew) / 2, Math.abs(w) / 2);
    const ry = Math.min(Math.abs(eh) / 2, Math.abs(h) / 2);
    if (rx <= 0 || ry <= 0) {
      this.drawRect(x, y, w, h);
      return;
    }
    const r = x + w;
    const b = y + h;
    this.moveTo(r, b - ry);
    this.curveTo(r, b, r - rx, b);
    this.lineTo(x + rx, b);
    this.curveTo(x, b, x, b - ry);
    this.lineTo(x, y + ry);
    this.curveTo(x, y, x + rx, y);
    this.lineTo(r - rx, y);
    this.curveTo(r, y, r, y + ry);
    this.lineTo(r, b - ry);
  }

  drawCircle(x: number, y: number, r: number): void {
    this.drawEllipse(x - r, y - r, r * 2, r * 2);
  }

  /** Flash approximates ellipses with 8 quadratic curves. */
  drawEllipse(x: number, y: number, w: number, h: number): void {
    const rx = w / 2;
    const ry = h / 2;
    const cx = x + rx;
    const cy = y + ry;
    const k = Math.tan(Math.PI / 8);
    const s = Math.SQRT1_2;
    this.moveTo(cx + rx, cy);
    const pts: Array<[number, number, number, number]> = [];
    for (let i = 0; i < 8; i++) {
      const a0 = (i * Math.PI) / 4;
      const a1 = ((i + 1) * Math.PI) / 4;
      const am = (a0 + a1) / 2;
      const ctrlR = 1 / Math.cos(Math.PI / 8);
      pts.push([cx + Math.cos(am) * rx * ctrlR, cy + Math.sin(am) * ry * ctrlR, cx + Math.cos(a1) * rx, cy + Math.sin(a1) * ry]);
    }
    void k;
    void s;
    for (const [c1, c2, a1, a2] of pts) this.curveTo(c1, c2, a1, a2);
  }

  copyFrom(src: Graphics): void {
    this.clear();
    for (const c of src.$commands) {
      switch (c.op) {
        case 'beginFill':
          this.beginFill(c.color, c.alpha);
          break;
        case 'beginGradientFill':
          this.beginGradientFill(c.type, c.colors, c.alphas, c.ratios, c.matrix ?? undefined, c.spreadMethod, c.interpolationMethod, c.focalPointRatio);
          break;
        case 'beginBitmapFill':
          this.beginBitmapFill(c.bitmap, c.matrix ?? undefined, c.repeat, c.smooth);
          break;
        case 'endFill':
          this.endFill();
          break;
        case 'lineStyle':
          this.lineStyle(c.thickness, c.color, c.alpha, c.pixelHinting, c.scaleMode, c.caps, c.joints, c.miterLimit);
          break;
        case 'moveTo':
          this.moveTo(c.x, c.y);
          break;
        case 'lineTo':
          this.lineTo(c.x, c.y);
          break;
        case 'curveTo':
          this.curveTo(c.cx, c.cy, c.x, c.y);
          break;
      }
    }
  }

  /** @internal Local bounds in twips. Fill-only moveTo points do not extend bounds. */
  $getBounds(): TRect {
    let b = this.$bounds;
    // Fills contribute their polygon points even without strokes.
    for (const f of this.$fills) {
      for (const poly of f.polys) {
        if (poly.length < 4) continue;
        for (let i = 0; i < poly.length; i += 2) {
          const px = Math.round(poly[i] * TWIPS);
          const py = Math.round(poly[i + 1] * TWIPS);
          b = rectUnion(b, { xMin: px, yMin: py, xMax: px, yMax: py });
        }
      }
    }
    return b;
  }

  /** @internal Even-odd fill hit test at a local pixel point. */
  $hitTest(lx: number, ly: number): boolean {
    for (const f of this.$fills) {
      let inside = false;
      for (const poly of f.polys) {
        const n = poly.length / 2;
        if (n < 3) continue;
        for (let i = 0, j = n - 1; i < n; j = i++) {
          const xi = poly[i * 2];
          const yi = poly[i * 2 + 1];
          const xj = poly[j * 2];
          const yj = poly[j * 2 + 1];
          if (yi > ly !== yj > ly && lx < ((xj - xi) * (ly - yi)) / (yj - yi) + xi) inside = !inside;
        }
      }
      if (inside) return true;
    }
    return false;
  }

  /** @internal */ get $isEmpty(): boolean {
    return this.$commands.length === 0 || !rectValid(this.$getBounds());
  }
}
